"use client";

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { usePermissions } from "@/context/permissions-context";
import { fetchData } from "@/utils/api-handler";
import { getIntegrationReferenceLabel } from "@/utils/integration-status";

interface IntegrationCatalogEntry {
  alias: string | null;
  reference: string | null;
  status: string | null;
}

export function useIntegrationCatalogMap(enabled = true) {
  const { hasAllPermissions } = usePermissions();
  const canListIntegrations = hasAllPermissions(["LIST_APP_INTEGRATIONS"]);

  const { data } = useInfiniteQuery<IntegrationListPageResponse>({
    queryKey: ["integrations-apps", "app-list"],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        limit: "50",
      });
      return fetchData(`/api/integrations/apps?${params.toString()}`);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) {
        return pages.length + 1;
      }
      return undefined;
    },
    enabled: enabled && canListIntegrations,
  });

  const catalogById = useMemo(() => {
    const map = new Map<string, IntegrationCatalogEntry>();
    (data?.pages.flatMap((page) => page.items) ?? []).forEach((item) => {
      map.set(item.id, {
        alias: item.alias?.trim() || null,
        reference: item.description?.trim() || null,
        status: item.status?.trim() || null,
      });
    });
    return map;
  }, [data]);

  const resolveIntegrationMeta = (
    integration: Pick<
      ConnectedIntegration,
      "integration_id" | "id" | "alias" | "integration_description"
    >
  ) => {
    const id = integration.integration_id ?? integration.id ?? "";
    const catalog = id ? catalogById.get(id) : undefined;
    const alias = integration.alias?.trim() || catalog?.alias || null;
    const reference = getIntegrationReferenceLabel(
      integration.integration_description,
      catalog?.reference
    );

    return { alias, reference };
  };

  const resolveIntegrationStatus = (
    integration: Pick<
      ConnectedIntegration,
      "integration_id" | "id" | "status"
    >
  ) => {
    const id = integration.integration_id ?? integration.id ?? "";
    const catalog = id ? catalogById.get(id) : undefined;
    const raw =
      integration.status?.trim() ||
      catalog?.status?.trim() ||
      "";
    return raw.toUpperCase();
  };

  return { catalogById, resolveIntegrationMeta, resolveIntegrationStatus };
}
